import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, getExecution } from '../../api';
import type { Environment, StoreMigration, StoreMigrationRisk } from '../../api';
import { useResource } from '../../hooks';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { formatBytes } from './projectHealth';
import { FactList } from './ui';
import { Callout, Checkbox, Field, Input, Section } from '../primitives';
import { EvidenceActions, OperationSummary, SummaryFact, SummaryFacts } from '../operation/OperationSummary';
import { actionLabel } from '../operation/actionLabels';

/**
 * ImportStorePanel brings a store that runs elsewhere into an environment:
 * its database dump, its media (a directory or a .tar/.tar.gz archive) and,
 * when the repository does not commit it, its app/etc/config.php. The files
 * are copied to the environment's server first; Commerce Ops only reads them
 * there. The flow is the existing-store migration of the API
 * (docs/EXISTING_STORE_MIGRATION.md): check the files, review what was found
 * and what the import will do, confirm, import, then deploy.
 */
export function ImportStorePanel({
  projectId,
  environment,
  magentoApplicationId,
  serverName,
  ready,
  runPath,
  onImported,
  onDeploy,
}: {
  projectId: string;
  environment: Environment;
  magentoApplicationId: string | null;
  serverName: string;
  /** The environment's setup has run: its database and media directory exist. */
  ready: boolean;
  /** A link to one run on the server, shown inside the Recovery section. */
  runPath: (executionId: string) => string;
  onImported: () => void;
  onDeploy: () => void;
}) {
  const migrations = useResource(() => api.listStoreMigrations(environment.id), [environment.id], environment.id);
  const latest = migrations.data?.migrations[0] ?? null;
  const [editing, setEditing] = useState(false);
  const inFlight = latest?.status === 'assessing' || latest?.status === 'importing';

  // The inspection and the import run on the server; the record follows
  // their runs, so it is read again until it settles.
  const { reload } = migrations;
  useEffect(() => {
    if (!inFlight) return;
    const timer = window.setInterval(reload, 3000);
    return () => window.clearInterval(timer);
  }, [inFlight, reload]);

  // Everything else on the page (deployments, the overview) changes once
  // an import has finished.
  const lastStatus = useRef(latest?.status);
  useEffect(() => {
    if (lastStatus.current === 'importing' && latest?.status === 'imported') onImported();
    lastStatus.current = latest?.status;
  }, [latest?.status, onImported]);

  const showForm = editing || !latest || latest.status === 'failed';

  return (
    <Section
      id="store-import-title"
      testId="store-import"
      title="Import an existing store"
      help={`Copy the files to ${serverName} first; Commerce Ops only reads them there.`}
      helpLabel="Before you start"
    >
      {!ready ? (
        <p className="panel__note muted" data-testid="store-import-not-ready">
          Available once the environment’s setup has run
        </p>
      ) : migrations.error && !migrations.data ? (
        <ErrorNotice error={migrations.error} />
      ) : !migrations.data ? (
        <p className="muted" role="status">Reading earlier imports…</p>
      ) : showForm ? (
        <HandOverForm
          environmentId={environment.id}
          environmentName={environment.name}
          runPath={runPath}
          serverName={serverName}
          initial={latest}
          failed={!editing && latest?.status === 'failed' ? latest : null}
          onCancel={latest && latest.status !== 'failed' ? () => setEditing(false) : undefined}
          onStarted={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : latest ? (
        <MigrationView
          migration={latest}
          projectId={projectId}
          environment={environment}
          magentoApplicationId={magentoApplicationId}
          serverName={serverName}
          runPath={runPath}
          onChanged={reload}
          onEditFiles={() => setEditing(true)}
          onDeploy={onDeploy}
        />
      ) : null}
    </Section>
  );
}

function HandOverForm({
  environmentId,
  environmentName,
  runPath,
  initial,
  failed,
  onCancel,
  onStarted,
}: {
  environmentId: string;
  environmentName: string;
  runPath: (executionId: string) => string;
  serverName: string;
  initial: StoreMigration | null;
  failed: StoreMigration | null;
  onCancel?: () => void;
  onStarted: () => void;
}) {
  const [dumpPath, setDumpPath] = useState(initial?.dump_path ?? '');
  const [mediaPath, setMediaPath] = useState(initial?.media_path ?? '');
  const [configPath, setConfigPath] = useState(initial?.config_path ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const ids = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const paths = [dumpPath, mediaPath, configPath].map((p) => p.trim());
    const fieldIds = [`${ids}-dump`, `${ids}-media`, `${ids}-config`];
    if (!paths[0] && !paths[1]) {
      setFieldError('Enter where the database dump or the media is on the server.');
      document.getElementById(fieldIds[0] ?? '')?.focus();
      return;
    }
    const relative = paths.findIndex((p) => p && !p.startsWith('/'));
    if (relative >= 0) {
      setFieldError('Enter full paths on the server, starting with /.');
      document.getElementById(fieldIds[relative] ?? '')?.focus();
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    setError(null);
    try {
      await api.assessStoreMigration(environmentId, {
        dump_path: paths[0] || undefined,
        media_path: paths[1] || undefined,
        config_path: paths[2] || undefined,
      });
      onStarted();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="store-import__form" onSubmit={submit} noValidate>
      {failed ? <FailedMigration migration={failed} environmentId={environmentId} environmentName={environmentName} runPath={runPath} onRetried={onStarted} /> : null}
      <div className="store-import__fields">
        <Field
          label="Database dump"
          hint={<>A .sql or .sql.gz made with mysqldump, tables only (no <code>--databases</code>).</>}
        >
          <Input
            id={`${ids}-dump`}
            name="dump_path"
            translate="no"
            value={dumpPath}
            onChange={(e) => setDumpPath(e.target.value)}
            placeholder="/srv/handover/prod.sql.gz"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field
          label="Media"
          hint="The old store’s pub/media as a directory, or a .tar, .tar.gz or .tgz of it."
        >
          <Input
            id={`${ids}-media`}
            name="media_path"
            translate="no"
            value={mediaPath}
            onChange={(e) => setMediaPath(e.target.value)}
            placeholder="/srv/handover/media.tar.gz"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field
          label="Module list (optional)"
          hint="The old store’s app/etc/config.php. Only needed when the repository does not include it."
        >
          <Input
            id={`${ids}-config`}
            name="config_path"
            translate="no"
            value={configPath}
            onChange={(e) => setConfigPath(e.target.value)}
            placeholder="/srv/handover/config.php"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
      </div>
      {fieldError ? (
        <Callout tone="danger" testId="store-import-field-error">
          {fieldError}
        </Callout>
      ) : null}
      <ErrorNotice error={error} />
      <div className="inline-form store-import__actions">
        <button className="button button--primary" type="submit" disabled={submitting}>
          {submitting ? 'Starting the check' : 'Check the files'}
        </button>
        {onCancel ? (
          <button className="button button--secondary" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

/** Step states that mean the step never started. */
const NOT_STARTED = new Set(['pending', 'ready', 'skipped', 'blocked', 'cancelled']);

/**
 * What the failed run did to the environment's database, only from the
 * import run's own steps. Without the run, the sentence says what is known:
 * the import had started, so the database may be partly imported.
 */
export function databaseAfterFailure(
  environmentName: string,
  importStarted: boolean,
  steps: { step_key: string; status: string }[] | null,
): string {
  if (!importStarted) return `Nothing was changed in ${environmentName}: the check only reads the files.`;
  const step = steps?.find((entry) => entry.step_key === 'database_import');
  if (!step) {
    return `The import had started, so the database of ${environmentName} may be partly imported. Open the run to see how far it got.`;
  }
  if (NOT_STARTED.has(step.status)) return `The database of ${environmentName} was not changed: the import stopped before its database step.`;
  if (step.status === 'succeeded') return `The database was imported into ${environmentName}; a later step failed. The database import is not repeated automatically.`;
  return `The database import into ${environmentName} started and did not finish, so its database may be partly imported. It is not retried automatically.`;
}

/**
 * FailedMigration: the one statement of a failed check or import. Outcome,
 * what it left in the environment's database, the cause as the run recorded
 * it, and the buttons to open the run and to try again.
 */
function FailedMigration({
  migration,
  environmentId,
  environmentName,
  runPath,
  onRetried,
}: {
  migration: StoreMigration;
  environmentId: string;
  environmentName: string;
  runPath: (executionId: string) => string;
  onRetried: () => void;
}) {
  const navigate = useNavigate();
  const importStarted = Boolean(migration.import_execution_id);
  const executionId = migration.import_execution_id || migration.assessment_execution_id;
  const run = useResource(
    () => (migration.import_execution_id ? getExecution(migration.import_execution_id) : Promise.resolve(null)),
    [migration.import_execution_id],
    migration.import_execution_id,
  );
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function retry() {
    setRetrying(true);
    setError(null);
    try {
      await api.assessStoreMigration(environmentId, {
        dump_path: migration.dump_path || undefined,
        media_path: migration.media_path || undefined,
        config_path: migration.config_path || undefined,
        source_path: migration.source_path || undefined,
      });
      onRetried();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRetrying(false);
    }
  }

  return (
    <OperationSummary
      tone="danger"
      title={`The last ${importStarted ? 'import' : 'check'} did not finish`}
      testId="store-import-failed"
      actions={
        <>
          <EvidenceActions onViewError={executionId ? () => navigate(runPath(executionId)) : undefined} errorTestId="store-import-view-error" />
          <button type="button" className="button button--secondary button--small" onClick={retry} disabled={retrying} data-testid="store-import-retry">
            {retrying ? 'Starting the check' : actionLabel.retry}
          </button>
        </>
      }
    >
      <SummaryFacts>
        <SummaryFact label="Database" testId="store-import-database">
          {databaseAfterFailure(environmentName, importStarted, run.data ? run.data.steps : null)}
        </SummaryFact>
        <SummaryFact label="Cause" testId="store-import-cause">
          {migration.last_error || 'The run on the server ended without a result.'}
        </SummaryFact>
        <SummaryFact label="Next step">
          Fix the cause, then retry. Retry checks the files again; an import is started from the review after it.
        </SummaryFact>
      </SummaryFacts>
      <ErrorNotice error={error} />
    </OperationSummary>
  );
}

const riskOrder: Record<string, number> = { blocking: 0, warning: 1, info: 2 };

function MigrationView({
  migration,
  projectId,
  environment,
  magentoApplicationId,
  serverName,
  runPath,
  onChanged,
  onEditFiles,
  onDeploy,
}: {
  migration: StoreMigration;
  projectId: string;
  environment: Environment;
  magentoApplicationId: string | null;
  serverName: string;
  runPath: (executionId: string) => string;
  onChanged: () => void;
  onEditFiles: () => void;
  onDeploy: () => void;
}) {
  const [recheckError, setRecheckError] = useState<Error | null>(null);
  const [rechecking, setRechecking] = useState(false);

  async function recheck() {
    setRechecking(true);
    setRecheckError(null);
    try {
      await api.assessStoreMigration(environment.id, {
        dump_path: migration.dump_path || undefined,
        media_path: migration.media_path || undefined,
        config_path: migration.config_path || undefined,
        source_path: migration.source_path || undefined,
      });
      onChanged();
    } catch (cause) {
      setRecheckError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRechecking(false);
    }
  }

  if (migration.status === 'assessing') {
    return (
      <RunInProgress
        text={`Checking the files on ${serverName}…`}
        detail="The dump is read once from start to end, so a large one takes a few minutes. Nothing is changed."
        executionId={migration.assessment_execution_id}
        runPath={runPath}
      />
    );
  }
  if (migration.status === 'importing') {
    return (
      <RunInProgress
        text={`Importing into ${environment.name}…`}
        detail="The store served from this environment is unavailable until the import has finished. The old store keeps serving until DNS moves."
        executionId={migration.import_execution_id}
        runPath={runPath}
      />
    );
  }
  if (migration.status === 'imported') {
    return (
      <div className="store-import__done" data-testid="store-import-imported">
        <Callout tone="success" title={`Imported into ${environment.name}`}>
          The deployment runs <code>setup:upgrade</code>, never a fresh install.
        </Callout>
        <Summary migration={migration} />
        <div className="inline-form">
          <button className="button button--primary" type="button" onClick={onDeploy}>
            Deploy
          </button>
          <button className="button button--secondary" type="button" onClick={onEditFiles}>
            Import again
          </button>
          {migration.import_execution_id ? (
            <Link className="panel__link" to={runPath(migration.import_execution_id)}>
              Full logs
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  // assessed, blocked or approved: the review.
  const risks = [...(migration.risks ?? [])].sort((a, b) => (riskOrder[a.level] ?? 3) - (riskOrder[b.level] ?? 3));
  const blocking = risks.filter((r) => r.level === 'blocking');
  const others = risks.filter((r) => r.level !== 'blocking');
  const needsKey = (migration.plan?.missing_inputs ?? []).includes('crypt_key');

  return (
    <div className="store-import__review" data-testid="store-import-review">
      <div className="store-import__columns">
        <section aria-labelledby="store-import-found">
          <h3 id="store-import-found" className="store-import__h3">What the files hold</h3>
          <Summary migration={migration} />
        </section>
        <section aria-labelledby="store-import-steps">
          <h3 id="store-import-steps" className="store-import__h3">What the import will do</h3>
          <ol className="store-import__steps">
            {(migration.plan?.steps ?? []).map((step) => (
              <li key={step.key}>{step.title}</li>
            ))}
          </ol>
        </section>
      </div>

      {blocking.length > 0 ? (
        <Callout tone="danger" title={`Fix ${blocking.length === 1 ? 'this' : 'these'} before importing`} testId="store-import-blocking">
          <RiskList risks={blocking} />
        </Callout>
      ) : null}

      {needsKey && magentoApplicationId ? (
        <EncryptionKeyForm
          projectId={projectId}
          applicationId={magentoApplicationId}
          environment={environment}
          onSaved={recheck}
        />
      ) : null}

      {others.length > 0 ? (
        <Callout tone="warning" title="Worth knowing" testId="store-import-notes">
          <RiskList risks={others} />
        </Callout>
      ) : null}

      <ErrorNotice error={recheckError} />

      {migration.status === 'blocked' ? (
        <div className="inline-form">
          <button className="button button--primary" type="button" onClick={recheck} disabled={rechecking}>
            {rechecking ? 'Starting the check' : 'Check again'}
          </button>
          <button className="button button--secondary" type="button" onClick={onEditFiles} disabled={rechecking}>
            Use other files
          </button>
        </div>
      ) : (
        <ImportAction
          migration={migration}
          environment={environment}
          onChanged={onChanged}
          onEditFiles={onEditFiles}
          onRecheck={recheck}
          rechecking={rechecking}
        />
      )}
    </div>
  );
}

function RunInProgress({
  text,
  detail,
  executionId,
  runPath,
}: {
  text: string;
  detail: string;
  executionId?: string;
  runPath: (executionId: string) => string;
}) {
  return (
    <div className="store-import__progress" role="status" data-testid="store-import-progress">
      <span className="spinner" aria-hidden="true" />
      <div>
        <strong>{text}</strong>
        <p className="muted">{detail}</p>
        {executionId ? (
          <Link className="panel__link" to={runPath(executionId)}>
            Full logs
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function RiskList({ risks }: { risks: StoreMigrationRisk[] }) {
  return (
    <ul className="store-import__risks">
      {risks.map((risk, index) => (
        <li key={`${risk.code}-${index}`}>{sentence(risk.message)}</li>
      ))}
    </ul>
  );
}

/** Risk messages come from the server in lower case; they read as sentences here. */
function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const capital = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function Summary({ migration }: { migration: StoreMigration }) {
  const a = migration.assessment ?? {};
  const facts: { label: string; value: ReactNode }[] = [];
  if (a.source?.version) facts.push({ label: 'Magento', value: [a.source.edition, a.source.version].filter(Boolean).join(' ') });
  if (migration.dump_path) {
    const db = a.database;
    const parts = [
      db?.sizeBytes ? formatBytes(db.sizeBytes) : null,
      typeof db?.tableCount === 'number' ? plural(db.tableCount, 'table', 'tables') : null,
      db?.hasSalesOrderTable ? 'orders included' : null,
    ].filter(Boolean);
    facts.push({
      label: 'Database dump',
      value: (
        <>
          <code>{migration.dump_path}</code>
          {parts.length ? <span className="muted"> — {parts.join(', ')}</span> : null}
        </>
      ),
    });
    const websites = db?.topology?.websites?.length ?? 0;
    if (websites > 1) facts.push({ label: 'Websites', value: `${websites}, reviewed under Infrastructure → Storefronts` });
  }
  if (migration.media_path) {
    const m = a.media;
    const parts = [
      typeof m?.files === 'number' ? plural(m.files, 'file', 'files') : null,
      m?.bytes ? formatBytes(m.bytes) : null,
      m?.archive ? (m.stripPrefix ? `archive, unpacked from ${m.stripPrefix}` : 'archive') : null,
    ].filter(Boolean);
    facts.push({
      label: 'Media',
      value: (
        <>
          <code>{migration.media_path}</code>
          {parts.length ? <span className="muted"> — {parts.join(', ')}</span> : null}
        </>
      ),
    });
  }
  if (migration.config_path) {
    const modules = a.configPhp?.modules;
    facts.push({
      label: 'Module list',
      value: (
        <>
          <code>{migration.config_path}</code>
          {typeof modules === 'number' ? <span className="muted"> — {plural(modules, 'module', 'modules')}</span> : null}
        </>
      ),
    });
  }
  const baseUrl = migration.plan?.base_urls?.secure || migration.plan?.base_urls?.unsecure;
  if (baseUrl) facts.push({ label: 'Magento answers on', value: <code>{baseUrl}</code> });
  return <FactList facts={facts} />;
}

function EncryptionKeyForm({
  projectId,
  applicationId,
  environment,
  onSaved,
}: {
  projectId: string;
  applicationId: string;
  environment: Environment;
  onSaved: () => void;
}) {
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const id = useId();

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!key.trim()) {
      setFieldError('Paste the key from the old store’s app/etc/env.php.');
      document.getElementById(`${id}-key`)?.focus();
      return;
    }
    setFieldError(null);
    setSaving(true);
    setError(null);
    try {
      const view = await api.getMagentoRuntimeConfig(projectId, applicationId, environment.id);
      await api.saveMagentoRuntimeConfig(projectId, applicationId, view.config, { crypt_key: key.trim() }, environment.id);
      setKey('');
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="store-import__key" onSubmit={save} noValidate data-testid="store-import-key">
      <Field
        label="The old store’s encryption key"
        hint={
          <>
            From its app/etc/env.php, <code>'crypt' =&gt; ['key' =&gt; …]</code>. Stored as a secret for {environment.name} and never shown again.
            Without it, saved payment tokens and integration passwords in the database cannot be read.
          </>
        }
      >
        <Input
          id={`${id}-key`}
          name="crypt_key"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={fieldError ? true : undefined}
        />
      </Field>
      {fieldError ? (
        <Callout tone="danger">{fieldError}</Callout>
      ) : null}
      <ErrorNotice error={error} />
      <div className="inline-form">
        <button className="button button--primary" type="submit" disabled={saving}>
          {saving ? 'Saving the key' : 'Save the key and check again'}
        </button>
      </div>
    </form>
  );
}

function ImportAction({
  migration,
  environment,
  onChanged,
  onEditFiles,
  onRecheck,
  rechecking,
}: {
  migration: StoreMigration;
  environment: Environment;
  onChanged: () => void;
  onEditFiles: () => void;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  const required = migration.plan?.required_confirmations ?? [];
  const approved = migration.status === 'approved';
  const needsReplace = required.includes('replace_existing_database');
  const [keyConfirmed, setKeyConfirmed] = useState(false);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const ids = useId();

  const confirmed =
    approved ||
    ((!required.includes('crypt_key_matches_existing_store') || keyConfirmed) && (!needsReplace || replaceConfirmed));

  async function runImport() {
    setBusy(true);
    setError(null);
    try {
      if (!approved) {
        await api.approveStoreMigration(environment.id, migration.id, {
          crypt_key_confirmed: keyConfirmed,
          replace_existing: replaceConfirmed,
        });
      }
      await api.importStoreMigration(environment.id, migration.id);
      setOpen(false);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="store-import__confirm">
      {!approved ? (
        <fieldset className="store-import__checks">
          <legend className="field__label">Before importing</legend>
          {required.includes('crypt_key_matches_existing_store') ? (
            <Checkbox
              id={`${ids}-key`}
              checked={keyConfirmed}
              onChange={(e) => setKeyConfirmed(e.target.checked)}
              label={<>The encryption key stored for {environment.name} is the old store’s key</>}
              hint="The one in its app/etc/env.php. A different key leaves encrypted data unreadable."
            />
          ) : null}
          {needsReplace ? (
            <Checkbox
              id={`${ids}-replace`}
              checked={replaceConfirmed}
              onChange={(e) => setReplaceConfirmed(e.target.checked)}
              label={<>Replace the Magento data already in {environment.name}’s database</>}
              hint="Its Magento tables are dropped before the dump is imported. Take a backup first if you may need them."
            />
          ) : null}
        </fieldset>
      ) : null}
      <ErrorNotice error={error} />
      <div className="inline-form">
        <button
          className={`button ${needsReplace ? 'button--danger-quiet' : 'button--primary'}`}
          type="button"
          onClick={() => setOpen(true)}
          disabled={!confirmed || rechecking}
          data-testid="store-import-start"
        >
          Import into {environment.name}
        </button>
        <button className="button button--secondary" type="button" onClick={onRecheck} disabled={rechecking}>
          {rechecking ? 'Starting the check' : 'Check again'}
        </button>
        <button className="button button--ghost" type="button" onClick={onEditFiles} disabled={rechecking}>
          Use other files
        </button>
      </div>
      <ConfirmDialog
        open={open}
        title={`Import the store into ${environment.name}?`}
        description={
          migration.plan?.downtime
            ? `${sentence(migration.plan.downtime)} The database import runs once and is not retried automatically.`
            : 'The database import runs once and is not retried automatically.'
        }
        confirmLabel={busy ? 'Starting the import' : `Import into ${environment.name}`}
        danger={needsReplace}
        busy={busy}
        onConfirm={runImport}
        onCancel={() => setOpen(false)}
      >
        <ol className="store-import__steps">
          {(migration.plan?.steps ?? [])
            .filter((step) => step.key !== 'deploy' && step.key !== 'cutover')
            .map((step) => (
              <li key={step.key}>{step.title}</li>
            ))}
        </ol>
        <ErrorNotice error={error} />
      </ConfirmDialog>
    </div>
  );
}
